import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQueries, useQuery } from "@tanstack/react-query";
import { EMPRESAS, EMPRESA_META, type Empresa } from "@brasilnovo/shared";
import { api } from "../lib/api.js";
import { ErrorBox, Spinner, num } from "../components/ui.js";
import {
  COLUNAS_PLANILHA, diasDaPlanilha, horarioLinha, ordenarPlanilha, paraTsv, patioComposto, valoresLinha,
  type LinhaCarga,
} from "../lib/planilha.js";

// Visão AUXILIAR da Torre: a mesma informação no formato exato da planilha de
// programação que a operação já usa (uma linha por carga, em sequência de
// data/hora). Somente leitura — vincular, confirmar horário e editar
// continuam na Torre de Controle, que segue sendo a tela principal.

const REFRESH_MS = 30_000;
const MESES = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];
const rotuloCompetencia = (c: string) => `${MESES[Number(c.slice(5, 7)) - 1]}/${c.slice(0, 4)}`;
const rotuloDia = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}`;

const FASES_FILTRO = {
  abertas: { label: "Disponíveis e agendadas", fases: ["DISPONIVEL", "AGENDADA"] },
  DISPONIVEL: { label: "Disponíveis", fases: ["DISPONIVEL"] },
  AGENDADA: { label: "Agendadas", fases: ["AGENDADA"] },
  FINALIZADA: { label: "Finalizadas", fases: ["FINALIZADA"] },
  todas: { label: "Todas", fases: ["DISPONIVEL", "AGENDADA", "FINALIZADA"] },
} as const;
type FiltroFase = keyof typeof FASES_FILTRO;

// Cores da coluna VEÍCULO como na planilha (U5 roxo, UK vermelho).
const VEICULO_CLS: Record<string, string> = { U5: "text-purple-700", UK: "text-red-700", UC: "text-fuchsia-700" };

const borda = "border border-slate-300";
const th = `${borda} bg-slate-100 px-2 py-1.5 text-center text-[11px] font-bold uppercase tracking-wide text-slate-700`;
const td = `${borda} px-2 py-1 whitespace-nowrap`;

export function Planilha() {
  const [competencia, setCompetencia] = useState<string | null>(null);
  const [empresa, setEmpresa] = useState<Empresa | "">("");
  const [filtroFase, setFiltroFase] = useState<FiltroFase>("abertas");
  const [dia, setDia] = useState("");
  const [busca, setBusca] = useState("");
  const [copiado, setCopiado] = useState<"ok" | "erro" | null>(null);

  const resumo = useQuery({
    queryKey: ["planilha-resumo"],
    queryFn: async () => (await api.get("/operacoes/torre")).data as { competencia: string | null; competencias: string[] },
  });
  const competenciaAtiva = competencia ?? resumo.data?.competencia ?? null;

  const consultas = useQueries({
    queries: FASES_FILTRO[filtroFase].fases.map((fase) => ({
      queryKey: ["planilha", competenciaAtiva, empresa, fase],
      enabled: !!competenciaAtiva,
      refetchInterval: REFRESH_MS,
      queryFn: async () => {
        const qs = new URLSearchParams({ fase, limit: "2000" });
        qs.set("ano", competenciaAtiva!.slice(0, 4));
        qs.set("mes", String(Number(competenciaAtiva!.slice(5, 7))));
        if (empresa) qs.set("empresa", empresa);
        return (await api.get(`/operacoes?${qs}`)).data as LinhaCarga[];
      },
    })),
  });
  const carregando = resumo.isLoading || consultas.some((q) => q.isLoading);
  const erro = resumo.error ?? consultas.find((q) => q.error)?.error;
  const atualizando = consultas.some((q) => q.isFetching);

  const todas = useMemo(() => ordenarPlanilha(consultas.flatMap((q) => q.data ?? [])), [consultas]);
  const dias = useMemo(() => diasDaPlanilha(todas), [todas]);
  const linhas = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return todas.filter((r) => {
      if (dia && horarioLinha(r).chave?.slice(0, 10) !== dia) return false;
      if (!termo) return true;
      return valoresLinha(r).join(" ").toLowerCase().includes(termo);
    });
  }, [todas, dia, busca]);

  const pesoTotal = linhas.reduce((a, r) => a + (Number(r.peso) || 0), 0);
  const urgentes = linhas.filter((r) => r.urgente).length;

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(paraTsv(linhas));
      setCopiado("ok");
    } catch {
      setCopiado("erro");
    }
    setTimeout(() => setCopiado(null), 2500);
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Torre · visão planilha</h1>
          <p className="mt-1 text-sm text-slate-500">
            As cargas no formato da planilha de programação, uma linha por carga, em ordem de data e hora.
            Para vincular, confirmar horário ou editar, use a <Link to="/torre" className="font-medium text-brand-700 hover:underline">Torre de Controle</Link>.
          </p>
        </div>
        <Link to="/torre" className="btn-secondary" data-testid="planilha-voltar-torre">Voltar à Torre</Link>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <select className="input w-40" data-testid="planilha-competencia" value={competenciaAtiva ?? ""} onChange={(e) => { setCompetencia(e.target.value); setDia(""); }}>
          {(resumo.data?.competencias ?? []).map((c) => <option key={c} value={c}>{rotuloCompetencia(c)}</option>)}
        </select>
        <select className="input w-40" data-testid="planilha-empresa" value={empresa} onChange={(e) => setEmpresa(e.target.value as Empresa | "")}>
          <option value="">Todas as empresas</option>
          {EMPRESAS.map((e) => <option key={e} value={e}>{EMPRESA_META[e].label}</option>)}
        </select>
        <select className="input w-52" data-testid="planilha-fase" value={filtroFase} onChange={(e) => setFiltroFase(e.target.value as FiltroFase)}>
          {(Object.keys(FASES_FILTRO) as FiltroFase[]).map((k) => <option key={k} value={k}>{FASES_FILTRO[k].label}</option>)}
        </select>
        <select className="input w-40" data-testid="planilha-dia" value={dia} onChange={(e) => setDia(e.target.value)}>
          <option value="">Todos os dias</option>
          {dias.map((d) => <option key={d} value={d}>{rotuloDia(d)}</option>)}
        </select>
        <input className="input w-60" data-testid="planilha-busca" placeholder="Buscar transporte, cliente, cidade…" value={busca} onChange={(e) => setBusca(e.target.value)} />
        <button type="button" className="btn-secondary ml-auto" onClick={copiar} disabled={!linhas.length} data-testid="planilha-copiar">
          {copiado === "ok" ? "Copiado ✓" : copiado === "erro" ? "Não foi possível copiar" : "Copiar para Excel"}
        </button>
      </div>

      <p className="mb-2 text-xs text-slate-500" data-testid="planilha-resumo">
        {num(linhas.length)} carga(s) · {num(pesoTotal)} t
        {urgentes > 0 && <> · <b className="text-red-700">{num(urgentes)} urgente(s)</b></>}
        {" · "}horário em <i>itálico</i> = solicitado, ainda não confirmado pela torre
        {atualizando && <span className="ml-2 text-brand-600">atualizando…</span>}
      </p>

      {erro ? <ErrorBox error={erro} />
        : carregando ? <Spinner />
        : (
          <div className="max-h-[70vh] overflow-auto rounded-lg border border-slate-300 bg-white">
            <table className="w-full border-collapse text-[12px] tabular-nums text-slate-900" data-testid="planilha-tabela">
              <thead className="sticky top-0 z-10">
                <tr>{COLUNAS_PLANILHA.map((c) => <th key={c} className={th}>{c}</th>)}</tr>
              </thead>
              <tbody>
                {linhas.length === 0 && (
                  <tr><td colSpan={COLUNAS_PLANILHA.length} className="px-4 py-8 text-center font-sans text-slate-400">Nenhuma carga neste filtro.</td></tr>
                )}
                {linhas.map((r) => {
                  const v = valoresLinha(r);
                  const h = horarioLinha(r);
                  const veiculo = (r.tipo_veiculo ?? "").trim().toUpperCase();
                  return (
                    <tr key={r.id} className="hover:bg-yellow-50" data-testid="planilha-linha" data-ordem={r.numero_ordem ?? ""}>
                      <td className={`${td} text-center font-bold ${VEICULO_CLS[veiculo] ?? "text-slate-700"}`}>{v[0]}</td>
                      <td className={`${td} ${h.solicitado ? "italic text-amber-700" : "font-semibold"}`} title={h.solicitado ? "Horário solicitado — aguardando confirmação da torre" : undefined}>{v[1]}</td>
                      <td className={`${td} text-center`}>{v[2]}</td>
                      <td className={`${td} text-right`}>{v[3]}</td>
                      <td className={`${td} text-center ${patioComposto(r.patio_coleta) ? "bg-slate-600 font-bold text-white" : ""}`}>{v[4]}</td>
                      <td className={`${td} max-w-[22rem] overflow-hidden text-ellipsis text-center`} title={v[5]}>{v[5]}</td>
                      <td className={`${td} text-center`}>{v[6]}</td>
                      <td className={`${td} text-center ${r.urgente ? "bg-red-600 font-bold text-white" : ""}`}>{v[7]}</td>
                      <td className={`${td} text-center ${v[8] !== "Normal" ? "bg-green-500 font-bold text-white" : ""}`}>{v[8]}</td>
                      <td className={`${td} text-center`}>{v[9]}</td>
                      <td className={`${td} text-center ${r.prioridade ? "bg-amber-300 font-bold" : ""}`}>{v[10]}</td>
                      <td className={`${td} text-center ${r.critica ? "bg-red-900 font-bold text-white" : ""}`}>{v[11]}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}
